import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
    /** The user's preference: 'light' | 'dark' | 'system'. */
    theme: Theme;
    /** What's actually rendered ('system' resolved against prefers-color-scheme). */
    resolvedTheme: 'light' | 'dark';
    setTheme: (theme: Theme) => void;
    toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'tef-theme';

function getSystemTheme(): 'light' | 'dark' {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(): Theme {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'light';
}

function applyTheme(resolved: 'light' | 'dark') {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
        readStoredTheme() === 'dark'
            ? 'dark'
            : readStoredTheme() === 'light'
                ? 'light'
                : getSystemTheme(),
    );

    useEffect(() => {
        const next = theme === 'system' ? getSystemTheme() : theme;
        setResolvedTheme(next);
        applyTheme(next);
        window.localStorage.setItem(STORAGE_KEY, theme);
    }, [theme]);

    // React to OS theme changes when in 'system' mode.
    useEffect(() => {
        if (theme !== 'system') return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => {
            const next = e.matches ? 'dark' : 'light';
            setResolvedTheme(next);
            applyTheme(next);
        };
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [theme]);

    const value: ThemeContextValue = {
        theme,
        resolvedTheme,
        setTheme: setThemeState,
        toggle: () => setThemeState(resolvedTheme === 'dark' ? 'light' : 'dark'),
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
    return ctx;
}
